type TypeEventListenerEntry = EventListener | EventListenerObject;

type TypeListeners = Record<string, TypeEventListenerEntry[] | undefined>;

// ----------------------------------------------------------------------------

class EventTargetManager implements EventTarget {
    #listeners: TypeListeners = {};

    getEventListeners(): TypeListeners {
        return Object.fromEntries(
            Object.entries(this.#listeners).map(([type, listeners]): any => [
                type,
                listeners == null ? undefined : [...listeners],
            ]),
        );
    }

    addEventListener(
        type: string,
        listener: EventListener | EventListenerObject | null,
        options?: AddEventListenerOptions | boolean,
    ): void {
        void options;

        if (listener == null) {
            return;
        }

        const found = this.#listeners[type];

        if (found == undefined) {
            this.#listeners[type] = [listener];
        } else if (!found.includes(listener)) {
            found.push(listener);
        }
    }

    removeEventListener(
        type: string,
        listener: EventListener | EventListenerObject | null,
        options?: EventListenerOptions | boolean,
    ): void {
        void options;

        if (listener == null) {
            return;
        }

        const found = this.#listeners[type];

        if (found == undefined) {
            return;
        }

        const stack = found;
        const stackLength = stack.length;

        for (let i = 0; i < stackLength; i++) {
            if (stack[i] === listener) {
                stack.splice(i, 1);
                return;
            }
        }
    }

    dispatchEvent(event: Event): boolean {
        const found = this.#listeners[event.type];

        if (found == undefined) {
            return !event.defaultPrevented;
        }

        const stack = found.slice();
        const stackLength = stack.length;

        for (let i = 0; i < stackLength; i++) {
            const stackItem = stack[i];

            if (typeof stackItem === 'function') {
                stackItem.call(this, event);
            } else if (typeof stackItem.handleEvent === 'function') {
                stackItem.handleEvent(event);
            }
        }

        return !event.defaultPrevented;
    }

    purgeEventListeners(): void {
        this.#listeners = {};
    }
}

// ----------------------------------------------------------------------------

export default EventTargetManager;
